import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Fragment } from "react";
import { useHistory, useParams } from "react-router-dom";

import { Divider, useToast } from "../../components";
import { useGetPartnerOffering } from "../../data/api";
import { buildPathWithParams, useQueryString } from "../../hooks";
import { uniqueBy } from "../../lib/js_utils/array_utils";
import { CapacityPicker } from "../capacity_picker";
import { PartnerOfferingLocationPicker } from "../partner_location_picker";
import { Spacer } from "../spacer";
import {
  useTrackCheckoutStartedEcommerceEventOnMount,
  useTrackOrderCompletedEcommerceEvent,
} from "../analytics_ecommerce_events";
import {
  Order,
  useLocationV2Query,
  useQuoteQuery,
  useSpaceOfferingsQuery,
  useSpacesQuery,
  useSpaceV2Query,
} from "../api";
import { MultiDayOfficeCheckoutBoxDesktop } from "../checkout_box_desktop";
import { pickerOverride } from "../checkout_box_mobile";
import { MultiPassPicker } from "../multipass_picker";
import {
  normalizeMultiDayOfficeOrderParams,
  toQuoteInput,
  useMultiDayOfficeOrderParamsHandlers,
} from "../order_params";
import {
  TemplateOfferingCheckoutTranzilaBase,
  useCheckoutTranzila,
} from "../template_offering_checkout_tranzila_base";
import { PartnerConfig } from "../partner_config";
import { getPartnerOfferingName } from "@/offering/get_partner_offering_name";

interface TemplateOfferingCheckoutTranzilaMultiDayOfficeParams {
  offeringID: string;
}

export interface TemplateOfferingCheckoutTranzilaMultiDayOfficeQueryParams {
  addOnID?: string;
  spaceOfferingID?: string;
  partnerOfferingLocationID: string;
}

export interface TemplateOfferingCheckoutTranzilaMultiDayOfficeProps {
  showTalkWithUsPrompt?: boolean;
  capacityOptionLabel?: string;
  partnerConfig: PartnerConfig;
  defaultMarketingConsent?: boolean;
}

export function TemplateOfferingCheckoutTranzilaMultiDayOffice(
  props: TemplateOfferingCheckoutTranzilaMultiDayOfficeProps
) {
  const {
    partnerConfig,
    showTalkWithUsPrompt,
    capacityOptionLabel,
    defaultMarketingConsent,
  } = props;
  const {
    tranzilaId,
    hubspotForm,
    favicon,
    termsURL,
    showLanguageSelector = false,
  } = partnerConfig;
  const pageParams =
    useParams<TemplateOfferingCheckoutTranzilaMultiDayOfficeParams>();
  const queryParams =
    useQueryString<TemplateOfferingCheckoutTranzilaMultiDayOfficeQueryParams>();
  const partnerOfferingID = pageParams.offeringID;
  const partnerOffering = useGetPartnerOffering(partnerOfferingID);
  const uniquePartnerOfferingLocations = useMemo(() => {
    return uniqueBy(partnerOffering.offeringLocations, (l) => l.location.id);
  }, [partnerOffering.offeringLocations]);

  const firstPartnerOfferingLocation = uniquePartnerOfferingLocations[0];
  const defaultSpaceID = firstPartnerOfferingLocation.spaceID;
  const {
    orderParams,
    orderParamsError,
    errorText,
    validateOrderParams,
    handleChangeLocation,
    handleChangeCapacity,
    handleChangeMultiPass,
    handleChangePromoCode,
    handleChangeSpace,
    handleChangeEmail,
  } = useMultiDayOfficeOrderParamsHandlers({
    ...normalizeMultiDayOfficeOrderParams({
      ...queryParams,
      partnerOfferingLocationID:
        uniquePartnerOfferingLocations.length > 1
          ? queryParams.partnerOfferingLocationID
          : firstPartnerOfferingLocation.id,
    }),
  });
  const [order, setOrder] = useState<Order>();
  const allSpaceIDs = useMemo(() => {
    return partnerOffering.offeringLocations.map((l) => l.spaceID);
  }, [partnerOffering.offeringLocations]);
  const allSpaces = useSpacesQuery(allSpaceIDs);
  const spaces = useMemo(() => {
    if (!orderParams.partnerOfferingLocationID) {
      return [];
    }

    return uniqueBy(allSpaces, (s) => s.id);
  }, [orderParams.partnerOfferingLocationID, allSpaces]);

  const capacities = useMemo(() => {
    const caps = [];

    for (const space of spaces) {
      for (const layout of space.layouts) {
        caps.push(layout.capacity);
      }
    }
    caps.sort((a, b) => a - b);
    return caps;
  }, [spaces]);

  const selectedSpaceID = useMemo(() => {
    const derivedSpace = spaces.find((s) => {
      return s.layouts.find((l) => l.capacity === orderParams.capacity);
    });

    return derivedSpace?.id;
  }, [spaces, orderParams.capacity]);

  const space = useSpaceV2Query(selectedSpaceID || defaultSpaceID);
  const location = useLocationV2Query(space?.locationID);
  const { data: spaceOfferings } = useSpaceOfferingsQuery(
    selectedSpaceID,
    partnerOffering.addOns.map((addOn) => addOn.id)
  );

  const toast = useToast();
  const history = useHistory();
  const [spaceLayoutID, setSpaceLayoutID] = useState<string>();
  useEffect(() => {
    if (space) setSpaceLayoutID(space.defaultLayoutID);
  }, [space, setSpaceLayoutID]);

  const quoteInput = useMemo(
    () =>
      selectedSpaceID && spaceLayoutID
        ? toQuoteInput(
            selectedSpaceID,
            partnerOffering.partner.id,
            orderParams,
            "WL",
            {
              layoutID: spaceLayoutID,
              attendees: 1,
            }
          )
        : null,
    [selectedSpaceID, partnerOffering.partner.id, orderParams, spaceLayoutID]
  );
  const {
    data: quote,
    error: quoteError,
    loading: quoteLoading,
  } = useQuoteQuery(quoteInput);

  useEffect(() => {
    if (selectedSpaceID) {
      handleChangeSpace(selectedSpaceID);
    }
  }, [selectedSpaceID, handleChangeSpace]);

  useTrackCheckoutStartedEcommerceEventOnMount(
    getPartnerOfferingName(partnerOffering),
    quote,
    space,
    location,
    undefined,
    partnerOffering
  );
  const trackOrderCompletedEcommerceEvent =
    useTrackOrderCompletedEcommerceEvent(partnerOffering);
  const handlePaymentSuccess = useCallback(() => {
    if (order) {
      trackOrderCompletedEcommerceEvent(
        getPartnerOfferingName(partnerOffering),
        order,
        space,
        location
      );

      const pathWithParams = buildPathWithParams(
        `/offerings/confirm_order/${partnerOffering.slug}/${order.id}`
      );
      history.push(pathWithParams);
    }
  }, [
    order,
    trackOrderCompletedEcommerceEvent,
    partnerOffering,
    space,
    location,
    history,
  ]);

  const requirePayment = quote?.totalPrice !== 0;
  const askForTax = (space?.bookingPolicy.askForTax && requirePayment) || false;
  const form = useCheckoutTranzila({
    requireBillingDetails: requirePayment,
    requirePayment: requirePayment,
    askForTax,
    initialValues: {
      fullName: "",
      companyName: "",
      phoneNumber: "",
      email: "",
      billingStreetAddress: "",
      billingCity: "",
      billingState: "",
      billingPostalCode: "",
      billingCountry: "",
      billingTax: "",
      termsChecked: false,
      marketingConsent: defaultMarketingConsent || false,
    },
    partnerID: partnerOffering.partner.id,
    partnerOfferingID: partnerOffering.id,
    legacy__details: {
      layoutID: spaceLayoutID || "",
      attendees: 1,
    },
    orderParams,
    onOrderSubmitted: (order) => {
      setOrder(order);
    },
    onError: (error) => {
      toast.error({
        message: error,
      });
    },
  });

  const disabledCheckout = !!(quoteLoading || quoteError);

  return (
    <TemplateOfferingCheckoutTranzilaBase
      disabledCheckout={disabledCheckout}
      showLanguageSelector={showLanguageSelector}
      localeOptions={partnerConfig.localeOptions}
      showTalkWithUsPrompt={showTalkWithUsPrompt}
      offeringName={getPartnerOfferingName(partnerOffering)}
      onPaymentSuccess={handlePaymentSuccess}
      partnerOfferingSlug={partnerOfferingID}
      tranzilaId={tranzilaId}
      requirePayment={requirePayment}
      order={order}
      onCloseTranzila={() => setOrder(undefined)}
      favicon={favicon}
      homepageURL={partnerOffering.partner.homepageUrl}
      termsURL={termsURL}
      hubspotForm={hubspotForm}
      location={location}
      space={space}
      partnerOffering={partnerOffering}
      askForTax={askForTax}
      orderParams={orderParams}
      orderParamsError={errorText}
      validateOrderParams={validateOrderParams}
      handleChangePromoCode={handleChangePromoCode}
      onPressBack={() => {
        if (history.length > 1) {
          history.goBack();
        } else {
          const pathWithParams = buildPathWithParams(
            `/offerings/${partnerOfferingID}`
          );
          history.replace(pathWithParams);
        }
      }}
      onChangeEmail={handleChangeEmail}
      quote={quote}
      mobileCheckoutBox={
        <Fragment>
          <PartnerOfferingLocationPicker
            value={orderParams.partnerOfferingLocationID}
            locations={uniquePartnerOfferingLocations}
            onChange={handleChangeLocation}
            override={pickerOverride}
          />
          <Spacer size={16} />
          <Divider />
          <Spacer size={16} />
          <CapacityPicker
            capacities={capacities}
            value={orderParams.capacity}
            onChange={handleChangeCapacity}
            override={pickerOverride}
            optionLabel={capacityOptionLabel}
          />
          <Spacer size={16} />
          <Divider />
          <Spacer size={16} />
          <MultiPassPicker
            multiPasses={spaceOfferings || []}
            value={orderParams.addOnID}
            onChange={handleChangeMultiPass}
            override={pickerOverride}
          />
        </Fragment>
      }
      desktopCheckoutBox={
        <MultiDayOfficeCheckoutBoxDesktop
          quoteLoading={quoteLoading}
          orderParams={orderParams}
          orderParamsErrors={orderParamsError}
          onChangeLocation={handleChangeLocation}
          onChangeMultiPass={handleChangeMultiPass}
          locations={uniquePartnerOfferingLocations}
          multiPasses={spaceOfferings || []}
          location={location}
          space={space}
          quote={quote}
          showCTA={false}
          capacities={capacities}
          onChangeCapacity={handleChangeCapacity}
          capacityOptionLabel={capacityOptionLabel}
        />
      }
      form={form}
    />
  );
}
